import React, { useEffect } from 'react'
import { X } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete'
import { useBonificacaoStore } from '@/stores/bonificacao-store'
import { useBonusAssessment } from '@/api/bonificacao-hooks'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export const PersonPickerBar: React.FC = () => {
  const selectedEmployeeId = useBonificacaoStore((s) => s.selectedEmployeeId)
  const selectedEmployeeInfo = useBonificacaoStore((s) => s.selectedEmployeeInfo)
  const setSelectedEmployee = useBonificacaoStore((s) => s.setSelectedEmployee)
  const setSelectedEmployeeInfo = useBonificacaoStore((s) => s.setSelectedEmployeeInfo)
  const currentAssessmentId = useBonificacaoStore((s) => s.currentAssessmentId)

  // Fallback re-fetch: if ID persisted but info was lost (legacy store without info persistence)
  const { data: assessment } = useBonusAssessment(currentAssessmentId, {
    enabled: !!selectedEmployeeId && !selectedEmployeeInfo && !!currentAssessmentId,
  })

  useEffect(() => {
    if (selectedEmployeeId && !selectedEmployeeInfo && assessment?.employee) {
      setSelectedEmployeeInfo({
        name: assessment.employee.name,
        department: assessment.employee.department,
        pictureUrl: assessment.employee.pictureUrl,
      })
    }
  }, [selectedEmployeeId, selectedEmployeeInfo, assessment, setSelectedEmployeeInfo])

  const handleClear = () => {
    setSelectedEmployee(null)
    setSelectedEmployeeInfo(null)
  }

  return (
    <div
      className="border-b border-gray-700/50 px-6 py-3 flex items-center gap-4"
      data-testid="person-picker-bar"
    >
      {selectedEmployeeId && selectedEmployeeInfo ? (
        <>
          <Avatar className="size-9">
            {selectedEmployeeInfo.pictureUrl ? (
              <AvatarImage src={selectedEmployeeInfo.pictureUrl} alt={selectedEmployeeInfo.name} />
            ) : null}
            <AvatarFallback className="bg-brand-blue text-white text-sm">
              {getInitials(selectedEmployeeInfo.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium truncate" data-testid="person-picker-name">{selectedEmployeeInfo.name}</div>
            {selectedEmployeeInfo.department && (
              <div className="text-brand-gray text-sm truncate">{selectedEmployeeInfo.department}</div>
            )}
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="p-1.5 rounded-md hover:bg-gray-700 transition-colors text-brand-gray hover:text-white"
            aria-label="Limpar seleção"
            data-testid="person-picker-clear"
          >
            <X className="size-4" />
          </button>
          <div className="w-px h-6 bg-gray-700" />
          <div className="w-64" data-testid="person-picker-search">
            <EmployeeAutocomplete
              onSelect={(employeeId, employee) => {
                setSelectedEmployee(employeeId)
                if (employee) {
                  setSelectedEmployeeInfo({
                    name: employee.name,
                    department: employee.department,
                    pictureUrl: employee.pictureUrl,
                  })
                }
              }}
              navigateOnSelect={false}
              placeholder="Trocar colaborador..."
            />
          </div>
        </>
      ) : (
        <div className="w-full max-w-md" data-testid="person-picker-search">
          <EmployeeAutocomplete
            onSelect={(employeeId, employee) => {
              setSelectedEmployee(employeeId)
              if (employee) {
                setSelectedEmployeeInfo({
                  name: employee.name,
                  department: employee.department,
                  pictureUrl: employee.pictureUrl,
                })
              }
            }}
            navigateOnSelect={false}
            placeholder="Selecionar colaborador..."
          />
        </div>
      )}
    </div>
  )
}
